/**
 * Auth Context — ported from Configurator/contexts/auth-context.tsx
 * Manages user authentication state. Reads auth_token from localStorage on mount,
 * calls GET /api/auth/me/, auto-refreshes on 401.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authAPI, type DjangoUser } from '../service/django-api';

interface AuthContextType {
  user: DjangoUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, firstName?: string, lastName?: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<DjangoUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { loadUser() }, []);

  async function loadUser() {
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
      }
    } catch {
      // Token expired — try refresh
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          await authAPI.refreshToken(refreshToken);
          const userData = await authAPI.getCurrentUser();
          setUser(userData);
        } catch {
          await authAPI.logout();
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function login(username: string, password: string) {
    const response = await authAPI.login(username, password);
    setUser(response.user);
  }

  async function register(username: string, email: string, password: string, firstName?: string, lastName?: string, phone?: string) {
    const response = await authAPI.register(username, email, password, firstName, lastName, phone);
    setUser(response.user);
  }

  async function logout() {
    await authAPI.logout();
    setUser(null);
  }

  async function refreshUser() { await loadUser() }

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      register,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
